import { WorkRequestAttachment } from 'src/app/types/attachments';
import { Component, OnInit, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { DxButtonModule, DxDataGridComponent, DxDataGridModule, DxDropDownButtonModule, DxFormModule, DxFileUploaderModule, DxLookupModule, DxLoadPanelModule, DxScrollViewModule, DxSelectBoxModule, DxTabPanelModule, DxTextAreaModule, DxValidationGroupModule } from 'devextreme-angular';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import { CardActivitiesModule, CardNotesModule, CardMessagesModule, StatusIndicatorModule, FormItemDateModule, FormTextboxModule } from 'src/app/components';
import { newWorkRequest, WorkRequest, WorkRequestStatusId, WorkRequestStatusIdNameMap } from 'src/app/types/work-request';
import { AuthService, DataService, IUser } from 'src/app/services';
import { DxToolbarModule } from 'devextreme-angular/ui/toolbar';
import { DxButtonTypes } from 'devextreme-angular/ui/button';
import { getSizeQualifier } from 'src/app/services/screen.service';
import notify from 'devextreme/ui/notify';
import { PagedResult } from 'src/app/types/paged-result';
import { forkJoin, Observable } from 'rxjs';

@Component({
	templateUrl: './work-request-details.component.html',
	styleUrls: ['./work-request-details.component.scss'],
})
export class WorkRequestDetailsComponent implements OnInit {
	private _activatedRoute: ActivatedRoute;
	private _authService: AuthService;
	private _dataService: DataService;
	private _user: IUser | null = null;
	private _workRequestId: number;
	private _savedData: WorkRequest;

	public workRequest: WorkRequest;
	public isLoading = false;
	public isEditing = false;
	public isReadOnly = true;
	public readonly workRequestStatusId = WorkRequestStatusId;
	public readonly workRequestStatusIdNameMap = WorkRequestStatusIdNameMap;
	public readonly getSizeQualifier = getSizeQualifier;
	public customers = [];
	public attachments: WorkRequestAttachment[] = [];
	public files: File[] = [];
	public allowedFileExtensions: string[] = ['.pdf','.doc','.docx','.jpg','.jpeg','.png','.txt', '.xls', '.xlsx', '.csv', '.zip', '.rar', '.7z'];
	public maxFileSize = 10 * 1024 * 1024; // 10 MB

	constructor(
		activatedRoute: ActivatedRoute, 
		authService: AuthService,
		dataService: DataService
	) {
		this._activatedRoute = activatedRoute;
		this._authService = authService;
		this._dataService = dataService;
		this._workRequestId = 0;
		this._savedData = null;
	}

	private loadData = () => {
		this._workRequestId = Number(this._activatedRoute.snapshot.paramMap.get('id'));

		this.isLoading = true;
		this._dataService.getLookUpUserCustomers().subscribe({
			next: (response) => {
				this.customers = response as any[];

				this._authService.getUser().then((e) => {
					this._user = e.data;
		
					if (this._workRequestId > 0) {
						this._dataService.getWorkRequest(this._workRequestId).subscribe({
							next: (response) => {
								this.workRequest = response;
								this.isLoading = false;
								this.isReadOnly = this.workRequest.statusId != WorkRequestStatusId.Requested;
//								console.log(this.workRequest);
							},
							error: (error) => {
								this.isLoading = false;
								notify(`Munkaigény bejelentés betöltése közben hiba lépett fel: ${error.error}`, 'error', 10000);
								console.error(error);
							},
						});
						this.loadAttachments();
					} else {
						this.workRequest = JSON.parse(JSON.stringify(newWorkRequest));
						this.workRequest.tenantId = this._dataService.tenantId;
						if (this.customers.length > 0)
							this.workRequest.customerId = this.customers[0].id;
						this.workRequest.timeStamp = new Date();
						this.workRequest.createdDate = new Date();
						this.isEditing = true;
						this.isReadOnly = false;
						this.isLoading = false;
//						console.log(this.workRequest);
//						console.table(this.workRequest);
//						console.log(this._user);
					}
				});
			}
		});
	};

	public ngOnInit(): void {
		this.loadData();
	}

	public handleRefresh = () => {
		this.isLoading = true;
		this.loadData();
	}

	public handleBackwards = () => {
		window.history.back();
	}

	public handleDelete = () => {
		const confirmResult = confirm('Biztosan törölni szeretné ezt az elemet?');
		if (!confirmResult)
			return;

		this._dataService.deleteWorkRequest(this.workRequest.id);

		window.history.back();
	}

	public handleEditButtonClick = () => {
		this._savedData = { ...this.workRequest }
		this.isEditing = true;
	};

	public handleSaveButtonClick = ({ validationGroup }: DxButtonTypes.ClickEvent) => {
		if(!validationGroup.validate().isValid) 
			return;

//		console.table(this.workRequest);

		this.isLoading = true;
		if (this._workRequestId === -1) {
			this._dataService.postWorkRequest(this.workRequest).subscribe({ 
				next: (response) => {
					this.workRequest = response;
					this._workRequestId = this.workRequest.id;
					this.saveAttachments();
				},
				error: (error) => {
					this.isLoading = false;
					notify(`Munkaigény bejelentés mentése közben hiba lépett fel: ${error.error}`, 'error', 10000);
					console.error(error);
				},
				complete: () => {
				},
			});
		} else {
			this._dataService.putWorkRequest(this.workRequest).subscribe({ 
				next: (response) => {
					this.workRequest = response;
					this._workRequestId = this.workRequest.id;
					this.saveAttachments();
				},
				error: (error) => {
					this.isLoading = false;
					notify(`Munkaigény bejelentés mentése közben hiba lépett fel: ${error.error}`, 'error', 10000);
					console.error(error);
				},
				complete: () => {
				},
			});
		}
	};

	private loadAttachments() {
		this.files = [];
		this.attachments = [];
		this._dataService.getWorkRequestAttachments(this._workRequestId).subscribe({
			next: (response) => {
				let result = response as unknown as PagedResult<WorkRequestAttachment>;
				for (const attachment of result.data) {
					let file = new File([], attachment.originalName + '.' + attachment.fileExtension);
//									file.size .size = attachment.size;
					this.files.push(file);
					attachment.file = file;
					this.attachments.push(attachment);
				}
			},
			error: (error) => {
				notify(`Munkaigény bejelentés csatolmányainak betöltése közben hiba lépett fel: ${error.error}`, 'error', 10000);
				console.error(error);
			}
		});
	}

	private saveAttachments() {
		const observables: Observable<any>[]= [];

		for (const file of this.files) {
			let attachment: WorkRequestAttachment | null = this.attachments.find(a => a.file === file) || null;
			if (attachment === null) {
				const extension = file.name.split('.').pop() || '';
				if (this.allowedFileExtensions.indexOf('.' + extension.toLowerCase()) === -1) {
					notify(`Fájl típusa nem engedélyezett: ${file.name}. Engedélyezett típusok: ${this.allowedFileExtensions.join(', ')}`, 'error', 10000);
					this.isLoading = false;
					return;
				}
				if (file.size > this.maxFileSize) {
					notify(`A fájl túl nagy: ${file.name}. A maximális méret ${this.maxFileSize / 1024 / 1024} MB.`, 'error', 10000);
					this.isLoading = false;
					return;
				}
			}
		}

		for (const attachment of this.attachments) {
			const file: File | null = this.files.find(f => f === attachment.file) || null;
			if (file === null) {
//				console.log('Deleting attachment: ', this.workRequest.id, attachment);
				observables.push(this._dataService.deleteWorkRequestAttachment(this.workRequest.id, attachment.fileStoreId));
			}
		}

		for (const file of this.files) {
			let attachment: WorkRequestAttachment | null = this.attachments.find(a => a.file === file) || null;
			if (attachment === null) {
				observables.push(this._dataService.postWorkRequestAttachment(this.workRequest.id, file));
			}
		}

		if (observables.length === 0) {
			this.isLoading = false;
			this.isEditing = false;
			notify('Munkaigény bejelentés sikeresen mentve.', 'success');
		} else {
			forkJoin(observables).subscribe({
				next: (responses) => {
					this.isLoading = false;
					this.isEditing = false;
					notify('Munkaigény bejelentés sikeresen mentve.', 'success');

					this.loadAttachments(); // Reload attachments to reflect changes
				},
				error: (error) => {
					this.isLoading = false;
//					this.isEditing = false;
					notify(`Munkaigény bejelentés csatolmányainak mentése közben hiba lépett fel: ${error.error}`, 'error', 10000);

					this.loadAttachments(); // Reload attachments to reflect changes
				}
			});
		}
	}

	public handleCancelButtonClick = () => {
		this.workRequest = { ...this._savedData };
		this.isEditing = false;
	};

// 	public handleFileSelected(event: any) {
// 		if (event.target.files && event.target.files.length > 0) {
// 			const file = event.target.files[0];
// //			console.log('Fájl kiválasztva: ', file);
// 		}
// 	}

	private hideUploadButtons() {
		const uploadButtons = document.querySelectorAll('.dx-fileuploader-upload-button');
		uploadButtons.forEach(btn => (btn as HTMLElement).style.display = 'none');
		const fileSizeLabels = document.querySelectorAll('.dx-fileuploader-file-size');
		fileSizeLabels.forEach(label => (label as HTMLElement).style.display = 'none');
	}

	public handleContentReady(e: any) {
		this.hideUploadButtons();
	}

	public handleFileUploaderValueChanged(e: any) {
		this.hideUploadButtons();
	}
}

@NgModule({
	imports: [
		DxButtonModule,
		DxDataGridModule,
		DxDropDownButtonModule,
		DxFileUploaderModule,
		DxFormModule,
		DxLoadPanelModule,
		DxLookupModule,
		DxScrollViewModule,
		DxSelectBoxModule,
		DxTabPanelModule,
		DxTextAreaModule,
		DxValidationGroupModule,
		DxToolbarModule,

		CardActivitiesModule,
		CardNotesModule,
		CardMessagesModule,
		FormItemDateModule, 
		FormTextboxModule,
		StatusIndicatorModule,
		CommonModule,
	],
	providers: [],
	exports: [],
	declarations: [WorkRequestDetailsComponent],
})
export class WorkRequestDetailsModule { }


